<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, isValid } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import type {
  EmitsToListeners,
  FormErrors,
} from '@/types/core';
import type {
  GetToolsPromoMatchesTeamsResponse,
  PostToolsPromoMatchesMatchesData,
} from '@/types/openapi-ts';
//#endregion

//#region types
interface Props {
  matchId?: string | null,
  leagueId: number | string | null,
  siteId: string,
  teamsList: GetToolsPromoMatchesTeamsResponse['payload'],
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface LandingMatchParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type FormType = {
  starts_at: string,
  team_home_id: number | null,
  team_away_id: number | null,
  page_url: string | null,
  odd_1?: number | null,
  odd_x?: number | null,
  odd_2?: number | null,
  update_odds_automatically: boolean,
};
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  matchId: '',
});
const emit = defineEmits<Emits>();
//#endregion

//#region const

//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const store = useTypedStore();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<FormErrors>({});

const utc = ref('UTC');
const form = ref<FormType>({
  starts_at: '',
  team_home_id: 0,
  team_away_id: 0,
  page_url: '',
  odd_1: null,
  odd_x: null,
  odd_2: null,
  update_odds_automatically: true,
});
//#endregion

//#region computed
const timezones = computed(() => store.getters['misc/timezones']);

const isEdit = computed(() => {
  return props.matchId;
});

const pPopupTitle = computed(() => {
  return `${t(`tools.landingLeagues.leagues.${isEdit.value ? 'edit' : 'Add'}`)} ${t('tools.match_popup.match')}`;
});
//#endregion

//#region methods
async function open() {
  utc.value = 'UTC';

  if(isEdit.value) {
    const { data: { payload } } = await api.getMatch(props.matchId!);
    const {
      team_home_id,
      team_away_id,
      page_url,
      odd_1,
      odd_2,
      odd_x,
      starts_at,
      update_odds_automatically,
    } = payload;

    form.value = {
      team_home_id,
      team_away_id,
      page_url,
      odd_1,
      odd_2,
      odd_x,
      starts_at,
      update_odds_automatically,
    };
  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};
  let promise;

  let params = {
    ...form.value,
    site_id: props.siteId,
    league_id: props.leagueId,
    odd_1: form.value.odd_1 || null,
    odd_2: form.value.odd_2 || null,
    odd_x: form.value.odd_x || null,
  } as unknown as PostToolsPromoMatchesMatchesData['body'];

  if(form.value.starts_at && isValid(new Date(form.value.starts_at))) {
    const defaultDate = format(form.value.starts_at, 'yyyy-MM-dd HH:mm:ss');
    const dateInTimeZone = formatInTimeZone(defaultDate, utc.value, 'yyyy-MM-dd HH:mm:ss zzz');
    const dateUTCFromTimeZone = toZonedTime(new Date(dateInTimeZone), 'UTC');
    params.starts_at = format(dateUTCFromTimeZone, 'yyyy-MM-dd HH:mm:ss');
  }

  if(isEdit.value) {
    promise = api.updateMatch(props.matchId!, params);
  } else {
    promise = api.createMatch(params);
  }

  promise.then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}
//#endregion

//#region watch

//#endregion

//#region hooks

//#endregion
defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="service-domain-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="t:space-x-16 t:flex t:items-start">
          <UiDatePicker
            v-model="form.starts_at"
            :label="$t('tools.match_popup.date_time')"
            :placeholder="$t(`tools.match_popup.placeholder_date_time`)"
            class="t:flex-1"
            :multi-calendars="false"
            :range="false"
            :always-clearable="false"
            external-format="dd MMM yyyy HH:mm"
            enable-time-picker
            teleport
            size="big"
            :error="errors.starts_at"
          />
          <UiSelectElement
            v-model="utc"
            :label="$t('tools.match_popup.timezone')"
            :placeholder="$t('tools.match_popup.placeholder_timezone')"
            class="t:flex-1"
            :list="timezones"
            option-value-param="zone"
            size="big"
          />
        </div>
        <div class="t:space-x-16 t:flex t:items-start">
          <UiSelectElement
            v-model="form.team_home_id"
            :disabled="teamsList.length === 0"
            :label="`${$t('tools.match_popup.team')} 1`"
            :placeholder="$t(`tools.match_popup.select_team`) + ' 1'"
            :list="teamsList"
            filterable
            size="big"
            :error="errors.team_home_id"
          />
          <UiSelectElement
            v-model="form.team_away_id"
            :disabled="teamsList.length === 0"
            :label="`${$t('tools.match_popup.team')} 2`"
            :placeholder="$t(`tools.match_popup.select_team`) + ' 2'"
            :list="teamsList"
            filterable
            size="big"
            :error="errors.team_away_id"
          />
        </div>
        <CardTitle :title="$t(`tools.match_popup.get_odds`)">
          <UiSwitch v-model="form.update_odds_automatically" />
        </CardTitle>
        <UiInput
          v-model="form.page_url"
          :label="$t(`tools.match_popup.link_btw`)"
          placeholder="https://betting.com/line/category/game.html"
          size="big"
          autosize
          :disabled="!form.update_odds_automatically"
          :error="errors.page_url"
        />
        <div class="t:gap-16 t:grid t:grid-cols-3">
          <UiInput
            v-model="form.odd_1"
            :label="$t(`tools.match_popup.odd`) + ' 1'"
            :placeholder="$t(`tools.match_popup.input_odd`) + ' 1'"
            size="big"
            autosize
            :error="errors.odd_1"
          />
          <UiInput
            v-model="form.odd_x"
            :label="$t(`tools.match_popup.odd`) + ' X'"
            :placeholder="$t(`tools.match_popup.input_odd`) + ' X'"
            size="big"
            autosize
            :error="errors.odd_x"
          />
          <UiInput
            v-model="form.odd_2"
            :label="$t(`tools.match_popup.odd`) + ' 2'"
            :placeholder="$t(`tools.match_popup.input_odd`) + ' 2'"
            size="big"
            autosize
            :error="errors.odd_2"
          />
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isLoading"
          @click="handleSave"
        >
          {{
            isEdit
              ? $t('tools.match_popup.save')
              : `${$t('tools.landingLeagues.leagues.Add')} ${$t('tools.match_popup.match')}`
          }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
